import React, { useRef, useState } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { ProductFilterFieldDef, ProductPillKey } from '../../utils/productBrowserFilterState';

export interface AddFilterMenuProps {
  /** Fields not already in the bar (Category / Store sync). */
  fields: ProductFilterFieldDef[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onAdd: (key: ProductPillKey) => void;
}

/**
 * The single affordance for every filter not currently applied. Only two
 * fields are ever behind this (Category, Store sync — the rest are pinned),
 * so this skips the search input the Staged Products version needed.
 */
export default function AddFilterMenu({ fields, open, onOpen, onClose, onAdd }: AddFilterMenuProps) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (fields.length === 0) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((prev) => (prev + delta + fields.length) % fields.length);
    } else if (event.key === 'Enter') {
      const field = fields[activeIndex];
      if (field) {
        event.preventDefault();
        onAdd(field.key);
      }
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        onClick={onOpen}
        disabled={fields.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: 38,
          px: 1.5,
          borderRadius: '4px',
          border: '1px dashed',
          borderColor: open ? 'primary.main' : 'grey.300',
          bgcolor: 'background.paper',
          color: open ? 'primary.dark' : 'text.secondary',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: '0.84375rem',
          fontWeight: 600,
          '&:hover': { borderColor: 'primary.main', color: 'primary.dark' },
          '&:disabled': { cursor: 'default', opacity: 0.5, borderColor: 'grey.300', color: 'text.disabled' },
        }}
      >
        <AddIcon sx={{ fontSize: 15 }} />
        <span>Add filter</span>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            onKeyDown: handleKeyDown,
            sx: {
              mt: 0.75,
              width: 280,
              p: 0.75,
              overflow: 'hidden',
              boxShadow: '0 14px 36px rgba(15, 23, 42, 0.15)',
            },
          },
        }}
      >
        <Box role="listbox" aria-label="Available filters" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {fields.map((field, index) => (
            <Box
              key={field.key}
              component="button"
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => onAdd(field.key)}
              onMouseEnter={() => setActiveIndex(index)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                width: '100%',
                flex: '0 0 auto',
                textAlign: 'left',
                border: 'none',
                borderRadius: '4px',
                px: 1.25,
                py: 1,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '0.84375rem',
                color: 'text.primary',
                bgcolor: index === activeIndex ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <span>{field.label}</span>
              {field.menuHint && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: field.menuHintColor === 'primary' ? 'primary.main' : 'text.disabled',
                  }}
                >
                  {field.menuHint}
                </Typography>
              )}
            </Box>
          ))}
          {fields.length === 0 && (
            <Typography variant="body2" sx={{ px: 1.25, py: 1, color: 'text.disabled' }}>
              Every filter is already applied
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
